/**
 * NhlEdgeHub — Page "NHL Edge Stats Hub"
 *
 * Tableau de bord analytique league-wide pour toutes les catégories NHL Edge™.
 * Fonctionnalités :
 *   - Sélecteur de saison et de type de match (saison régulière / éliminatoires)
 *   - Filtre par équipe NHL
 *   - Navigation par onglets entre les catégories
 *   - Classement joueurs cliquables → page Fiche Joueur
 *   - États loading / erreur / vide
 *   - Dark mode natif, responsive
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Gauge,
  Zap,
  Navigation,
  Target,
  Trophy,
  Shield,
  BarChart2,
  TrendingUp,
  ChevronRight,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { useLanguageStore } from '../lib/languageStore';
import { useThemeStore } from '../lib/themeStore';
import { usePageTracking } from '../lib/hooks/usePageTracking';
import {
  fetchEdgeLeaderboard,
  EdgeCategory,
  EdgeLeaderEntry,
  SkaterEdgeCategory,
  GoalieEdgeCategory,
} from '../lib/api/nhlEdgeLeaderboard';
import { TeamLogo } from '../components/TeamLogo/TeamLogo';
import { fetchEdgeTeamDetail, resolveTeamIdFromAbbrev } from '../lib/api/nhlEdgeTeam';
import TeamDetailPage from './TeamDetailPage';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEASONS = [
  { value: '20252026', label: '2025/2026' },
  { value: '20242025', label: '2024/2025' },
  { value: '20232024', label: '2023/2024' },
];

const NHL_TEAMS = [
  { abbrev: 'ANA', name: 'Anaheim Ducks' },
  { abbrev: 'BOS', name: 'Boston Bruins' },
  { abbrev: 'BUF', name: 'Buffalo Sabres' },
  { abbrev: 'CAR', name: 'Carolina Hurricanes' },
  { abbrev: 'CBJ', name: 'Columbus Blue Jackets' },
  { abbrev: 'CGY', name: 'Calgary Flames' },
  { abbrev: 'CHI', name: 'Chicago Blackhawks' },
  { abbrev: 'COL', name: 'Colorado Avalanche' },
  { abbrev: 'DAL', name: 'Dallas Stars' },
  { abbrev: 'DET', name: 'Detroit Red Wings' },
  { abbrev: 'EDM', name: 'Edmonton Oilers' },
  { abbrev: 'FLA', name: 'Florida Panthers' },
  { abbrev: 'LAK', name: 'Los Angeles Kings' },
  { abbrev: 'MIN', name: 'Minnesota Wild' },
  { abbrev: 'MTL', name: 'Montréal Canadiens' },
  { abbrev: 'NSH', name: 'Nashville Predators' },
  { abbrev: 'NJD', name: 'New Jersey Devils' },
  { abbrev: 'NYI', name: 'New York Islanders' },
  { abbrev: 'NYR', name: 'New York Rangers' },
  { abbrev: 'OTT', name: 'Ottawa Senators' },
  { abbrev: 'PHI', name: 'Philadelphia Flyers' },
  { abbrev: 'PIT', name: 'Pittsburgh Penguins' },
  { abbrev: 'SEA', name: 'Seattle Kraken' },
  { abbrev: 'SJS', name: 'San Jose Sharks' },
  { abbrev: 'STL', name: 'St. Louis Blues' },
  { abbrev: 'TBL', name: 'Tampa Bay Lightning' },
  { abbrev: 'TOR', name: 'Toronto Maple Leafs' },
  { abbrev: 'UTA', name: 'Utah Hockey Club' },
  { abbrev: 'VAN', name: 'Vancouver Canucks' },
  { abbrev: 'VGK', name: 'Vegas Golden Knights' },
  { abbrev: 'WSH', name: 'Washington Capitals' },
  { abbrev: 'WPG', name: 'Winnipeg Jets' },
];

// ---------------------------------------------------------------------------
// Category definitions
// ---------------------------------------------------------------------------

interface CategoryDef {
  id: EdgeCategory;
  icon: React.ReactNode;
  labelFr: string;
  labelEn: string;
  accentColor: string;
  group: 'skater' | 'goalie';
}

const SKATER_CATEGORIES: CategoryDef[] = [
  {
    id: 'skating-speed' as SkaterEdgeCategory,
    icon: <Gauge size={16} />,
    labelFr: 'Vitesse de patinage',
    labelEn: 'Skating Speed',
    accentColor: '#0ea5e9',
    group: 'skater',
  },
  {
    id: 'bursts-22' as SkaterEdgeCategory,
    icon: <Zap size={16} />,
    labelFr: 'Accélérations ≥ 22 mph',
    labelEn: 'Bursts ≥ 22 mph',
    accentColor: '#f59e0b',
    group: 'skater',
  },
  {
    id: 'bursts-20' as SkaterEdgeCategory,
    icon: <Zap size={16} />,
    labelFr: 'Accélérations 20–22 mph',
    labelEn: 'Bursts 20–22 mph',
    accentColor: '#f97316',
    group: 'skater',
  },
  {
    id: 'shot-speed' as SkaterEdgeCategory,
    icon: <Target size={16} />,
    labelFr: 'Vitesse de tir',
    labelEn: 'Shot Speed',
    accentColor: '#ec4899',
    group: 'skater',
  },
  {
    id: 'skating-distance' as SkaterEdgeCategory,
    icon: <Navigation size={16} />,
    labelFr: 'Distance patinée',
    labelEn: 'Skating Distance',
    accentColor: '#10b981',
    group: 'skater',
  },
  {
    id: 'offensive-zone' as SkaterEdgeCategory,
    icon: <Activity size={16} />,
    labelFr: 'Temps zone offensive',
    labelEn: 'Offensive Zone Time',
    accentColor: '#6366f1',
    group: 'skater',
  },
  {
    id: 'sog-high-danger' as SkaterEdgeCategory,
    icon: <BarChart2 size={16} />,
    labelFr: 'TCC zone danger',
    labelEn: 'SOG — High Danger',
    accentColor: '#a855f7',
    group: 'skater',
  },
  {
    id: 'goals-high-danger' as SkaterEdgeCategory,
    icon: <Trophy size={16} />,
    labelFr: 'Buts zone danger',
    labelEn: 'Goals — High Danger',
    accentColor: '#14b8a6',
    group: 'skater',
  },
];

const GOALIE_CATEGORIES: CategoryDef[] = [
  {
    id: 'gaa' as GoalieEdgeCategory,
    icon: <Shield size={16} />,
    labelFr: 'Moy. buts alloués',
    labelEn: 'Goals Against Average',
    accentColor: '#6366f1',
    group: 'goalie',
  },
  {
    id: 'games-above-900' as GoalieEdgeCategory,
    icon: <Trophy size={16} />,
    labelFr: 'Matchs > .900',
    labelEn: 'Games Above .900',
    accentColor: '#10b981',
    group: 'goalie',
  },
  {
    id: 'goal-differential' as GoalieEdgeCategory,
    icon: <TrendingUp size={16} />,
    labelFr: 'Diff. buts / 60',
    labelEn: 'Goal Differential / 60',
    accentColor: '#0ea5e9',
    group: 'goalie',
  },
  {
    id: 'goal-support' as GoalieEdgeCategory,
    icon: <Target size={16} />,
    labelFr: 'Soutien offensif',
    labelEn: 'Goal Support',
    accentColor: '#f59e0b',
    group: 'goalie',
  },
  {
    id: 'point-pctg' as GoalieEdgeCategory,
    icon: <Activity size={16} />,
    labelFr: '% Points',
    labelEn: 'Point %',
    accentColor: '#a855f7',
    group: 'goalie',
  },
];

const ALL_CATEGORIES = [...SKATER_CATEGORIES, ...GOALIE_CATEGORIES];

// ---------------------------------------------------------------------------
// Leaderboard state per category
// ---------------------------------------------------------------------------

type CategoryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; entries: EdgeLeaderEntry[]; unit?: string }
  | { status: 'error'; message: string };

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function NhlEdgeHub() {
  // Replace the original hub with the Team Detail page for now
  return <TeamDetailPage />;
}

// ---------------------------------------------------------------------------
// CategoryMenu — sidebar + mobile dropdown content
// ---------------------------------------------------------------------------

function CategoryMenu({
  activeCategory,
  onSelect,
  fr,
}: {
  activeCategory: EdgeCategory;
  onSelect: (cat: EdgeCategory) => void;
  fr: boolean;
}) {
  return (
    <div className="py-2">
      {/* Skater section */}
      <div
        className="px-3 py-2 small fw-bold text-uppercase"
        style={{ letterSpacing: '0.08em', color: '#0ea5e9', fontSize: '0.7rem' }}
      >
        {fr ? '🏒 Patineurs' : '🏒 Skaters'}
      </div>
      {SKATER_CATEGORIES.map((cat) => (
        <CategoryItem
          key={cat.id}
          cat={cat}
          isActive={activeCategory === cat.id}
          fr={fr}
          onSelect={onSelect}
        />
      ))}
      {/* Goalie section */}
      <div
        className="px-3 py-2 small fw-bold text-uppercase mt-2"
        style={{ letterSpacing: '0.08em', color: '#6366f1', fontSize: '0.7rem' }}
      >
        {fr ? '🥅 Gardiens' : '🥅 Goalies'}
      </div>
      {GOALIE_CATEGORIES.map((cat) => (
        <CategoryItem
          key={cat.id}
          cat={cat}
          isActive={activeCategory === cat.id}
          fr={fr}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function CategoryItem({
  cat,
  isActive,
  fr,
  onSelect,
}: {
  cat: CategoryDef;
  isActive: boolean;
  fr: boolean;
  onSelect: (cat: EdgeCategory) => void;
}) {
  return (
    <button
      className="btn w-100 d-flex align-items-center gap-2 px-3 py-2 rounded-0"
      onClick={() => onSelect(cat.id)}
      style={{
        background: isActive
          ? `${cat.accentColor}22`
          : 'transparent',
        borderLeft: isActive ? `3px solid ${cat.accentColor}` : '3px solid transparent',
        color: isActive ? cat.accentColor : undefined,
        fontWeight: isActive ? 600 : 400,
        fontSize: '0.875rem',
        textAlign: 'left',
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <span style={{ color: cat.accentColor, flexShrink: 0 }}>{cat.icon}</span>
      <span>{fr ? cat.labelFr : cat.labelEn}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// LeaderboardPanel
// ---------------------------------------------------------------------------

function LeaderboardPanel({
  categoryDef,
  state,
  fr,
  isDark,
  onPlayerClick,
  onRetry,
  teamAbbrev,
}: {
  categoryDef: CategoryDef;
  state: CategoryState;
  fr: boolean;
  isDark: boolean;
  onPlayerClick: (id: number) => void;
  onRetry: () => void;
  teamAbbrev: string;
}) {
  return (
    <div
      className="card shadow-sm h-100"
      style={{
        background: isDark ? 'rgba(15,23,42,0.8)' : 'rgba(248,250,252,0.95)',
        border: `1px solid ${categoryDef.accentColor}30`,
        borderTop: `3px solid ${categoryDef.accentColor}`,
      }}
    >
      {/* Panel header */}
      <div
        className="card-header bg-transparent d-flex align-items-center justify-content-between"
        style={{ borderBottom: `1px solid ${categoryDef.accentColor}25` }}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={{ color: categoryDef.accentColor }}>{categoryDef.icon}</span>
          <div>
            <h2 className="h5 mb-0 fw-bold">
              {fr ? categoryDef.labelFr : categoryDef.labelEn}
            </h2>
            <p className="text-muted small mb-0">
              {teamAbbrev
                ? fr ? `Filtré · ${teamAbbrev}` : `Filtered · ${teamAbbrev}`
                : fr ? 'Top 20 — Ligue' : 'Top 20 — League'}
            </p>
          </div>
        </div>

        {state.status === 'error' && (
          <button className="btn btn-sm btn-outline-warning" onClick={onRetry}>
            <RefreshCw size={14} className="me-1" />
            {fr ? 'Réessayer' : 'Retry'}
          </button>
        )}
      </div>

      <div className="card-body p-0">
        {state.status === 'loading' && <LeaderboardSkeleton />}

        {state.status === 'error' && (
          <ErrorState message={state.message} fr={fr} onRetry={onRetry} />
        )}

        {(state.status === 'success' || state.status === 'idle') && state.status === 'success' && (
          <>
            {state.entries.length === 0 ? (
              <EmptyState fr={fr} teamAbbrev={teamAbbrev} />
            ) : (
              <LeaderboardTable
                entries={state.entries}
                unit={state.unit}
                accentColor={categoryDef.accentColor}
                isDark={isDark}
                fr={fr}
                onPlayerClick={onPlayerClick}
              />
            )}
          </>
        )}

        {state.status === 'idle' && (
          <div className="d-flex align-items-center justify-content-center py-5 text-muted">
            <Activity size={20} className="me-2 opacity-50" />
            <span className="small">{fr ? 'Sélectionnez une catégorie' : 'Select a category'}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeaderboardTable
// ---------------------------------------------------------------------------

function LeaderboardTable({
  entries,
  unit,
  accentColor,
  isDark,
  fr,
  onPlayerClick,
}: {
  entries: EdgeLeaderEntry[];
  unit?: string;
  accentColor: string;
  isDark: boolean;
  fr: boolean;
  onPlayerClick: (id: number) => void;
}) {
  return (
    <div className="table-responsive">
      <table className="table table-hover mb-0">
        <thead>
          <tr
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
            }}
          >
            <th className="ps-3 py-3 text-muted fw-semibold" style={{ width: 44 }}>
              #
            </th>
            <th className="py-3 text-muted fw-semibold">
              {fr ? 'Joueur' : 'Player'}
            </th>
            <th className="py-3 text-muted fw-semibold text-center" style={{ width: 80 }}>
              {fr ? 'Éq.' : 'Team'}
            </th>
            <th className="py-3 text-muted fw-semibold text-center" style={{ width: 60 }}>
              Pos
            </th>
            <th className="pe-3 py-3 text-muted fw-semibold text-end" style={{ minWidth: 90 }}>
              {unit || '—'}
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => (
            <LeaderboardRow
              key={entry.playerId}
              entry={entry}
              rank={idx + 1}
              accentColor={accentColor}
              isDark={isDark}
              onClick={() => onPlayerClick(entry.playerId)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeaderboardRow
// ---------------------------------------------------------------------------

function LeaderboardRow({
  entry,
  rank,
  accentColor,
  isDark,
  onClick,
}: {
  entry: EdgeLeaderEntry;
  rank: number;
  accentColor: string;
  isDark: boolean;
  onClick: () => void;
}) {
  const rankBadgeStyle =
    rank === 1
      ? { background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', color: '#000' }
      : rank === 2
      ? { background: 'linear-gradient(135deg, #94a3b8, #cbd5e1)', color: '#000' }
      : rank === 3
      ? { background: 'linear-gradient(135deg, #cd7c32, #d97706)', color: '#fff' }
      : {
          background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
          color: isDark ? '#94a3b8' : '#64748b',
        };

  const headshotUrl =
    entry.headshot ||
    `https://assets.nhle.com/mugs/nhl/20232024/${entry.teamAbbrev}/${entry.playerId}.png`;

  return (
    <tr
      onClick={onClick}
      style={{
        cursor: 'pointer',
        transition: 'background 0.12s',
      }}
      className="align-middle"
    >
      {/* Rank */}
      <td className="ps-3 py-2">
        <span
          className="badge rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
          style={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            ...rankBadgeStyle,
          }}
        >
          {rank}
        </span>
      </td>

      {/* Player */}
      <td className="py-2">
        <div className="d-flex align-items-center gap-2">
          <img
            src={headshotUrl}
            alt={`${entry.firstName} ${entry.lastName}`}
            width={36}
            height={36}
            className="rounded-circle flex-shrink-0"
            style={{ objectFit: 'cover', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'https://assets.nhle.com/mugs/nhl/default-skater.png';
            }}
          />
          <div>
            <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
              {entry.firstName} {entry.lastName}
            </div>
            {entry.secondaryValues && entry.secondaryValues.length > 0 && (
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                {entry.secondaryValues.map((s) => `${s.label}: ${s.value}`).join(' · ')}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Team */}
      <td className="py-2 text-center">
        <TeamLogo abbrev={entry.teamAbbrev} size={28} />
      </td>

      {/* Position */}
      <td className="py-2 text-center">
        <span
          className="badge"
          style={{
            background: `${accentColor}22`,
            color: accentColor,
            fontSize: '0.7rem',
            fontWeight: 600,
          }}
        >
          {entry.positionCode || '—'}
        </span>
      </td>

      {/* Value */}
      <td className="pe-3 py-2 text-end">
        <span
          className="fw-bold"
          style={{
            fontSize: '1rem',
            color: rank <= 3 ? accentColor : undefined,
          }}
        >
          {entry.primaryValue}
        </span>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

// Pre-computed deterministic skeleton widths
const SKELETON_WIDTHS = [72, 58, 85, 63, 79, 55, 68, 81, 60, 75];

function LeaderboardSkeleton() {
  return (
    <div className="p-3">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="d-flex align-items-center gap-3 mb-3"
          style={{ opacity: 1 - i * 0.07 }}
        >
          <div
            className="rounded-circle flex-shrink-0"
            style={{
              width: 28,
              height: 28,
              background: 'var(--bs-gray-700, rgba(148,163,184,0.2))',
            }}
          />
          <div
            className="rounded-circle flex-shrink-0"
            style={{
              width: 36,
              height: 36,
              background: 'var(--bs-gray-700, rgba(148,163,184,0.2))',
            }}
          />
          <div className="flex-grow-1">
            <div
              className="rounded mb-1"
              style={{
                height: 14,
                width: `${SKELETON_WIDTHS[i]}%`,
                background: 'var(--bs-gray-700, rgba(148,163,184,0.2))',
              }}
            />
            <div
              className="rounded"
              style={{
                height: 10,
                width: '30%',
                background: 'var(--bs-gray-700, rgba(148,163,184,0.15))',
              }}
            />
          </div>
          <div
            className="rounded flex-shrink-0"
            style={{
              height: 22,
              width: 52,
              background: 'var(--bs-gray-700, rgba(148,163,184,0.2))',
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorState({ message, fr, onRetry }: { message: string; fr: boolean; onRetry: () => void }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3 text-center px-4">
      <div
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: 56,
          height: 56,
          background: 'rgba(245,158,11,0.12)',
        }}
      >
        <Activity size={24} style={{ color: '#f59e0b' }} />
      </div>
      <div>
        <p className="fw-semibold mb-1">
          {fr ? 'Données indisponibles' : 'Data unavailable'}
        </p>
        <p className="text-muted small mb-0">{message}</p>
      </div>
      <button className="btn btn-sm btn-outline-warning" onClick={onRetry}>
        <RefreshCw size={13} className="me-1" />
        {fr ? 'Réessayer' : 'Retry'}
      </button>
      <p className="text-muted" style={{ fontSize: '0.72rem', maxWidth: 320 }}>
        {fr
          ? 'Les données NHL Edge peuvent être temporairement indisponibles pour cette saison ou ce type de match.'
          : 'NHL Edge data may be temporarily unavailable for this season or game type.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ fr, teamAbbrev }: { fr: boolean; teamAbbrev: string }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-2 text-center px-4">
      <BarChart2 size={36} className="opacity-25" />
      <p className="fw-semibold mb-1">
        {fr ? 'Aucun résultat' : 'No results found'}
      </p>
      <p className="text-muted small mb-0">
        {teamAbbrev
          ? fr
            ? `Aucune donnée Edge disponible pour ${teamAbbrev} dans cette catégorie.`
            : `No Edge data available for ${teamAbbrev} in this category.`
          : fr
          ? 'Aucune donnée Edge disponible pour cette catégorie cette saison.'
          : 'No Edge data available for this category this season.'}
      </p>
    </div>
  );
}
